import React, { useState } from "react";

export default function WhatsAppAnalysis() {
  const [chatText, setChatText] = useState(
    `Rohit: Hey Amit, are we meeting today?
Amit: Yes, let's meet at 7 PM.
Rohit: Perfect.`
  );

  const [intent, setIntent] = useState("Summarize");
  const [language, setLanguage] = useState("English");
  const [result, setResult] = useState("");
  const [maskedPreview, setMaskedPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  // Simple masking for WhatsApp names (same pattern as Email but WhatsApp-style)
  function maskNames(text) {
    const nameRegex = /^([A-Z][a-zA-Z]{2,20})(?=:)/gm;
    let counter = 1;
    const map = {};

    return text.replace(nameRegex, (match) => {
      if (!map[match]) map[match] = `Person${counter++}`;
      return map[match];
    });
  }

  const onSend = async () => {
    setLoading(true);
    setResult("");
    setSaveStatus(null);

    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          chatText,
          intent,
          language,
        }),
      });

      if (!res.ok) throw new Error("API Error");

      const data = await res.json();

      setResult(data.result || "No result from AI");
      setMaskedPreview(data.masked || maskNames(chatText));
    } catch (err) {
      const masked = maskNames(chatText);
      setMaskedPreview(masked);

      let fallback = "";
      if (/summar/i.test(intent)) {
        fallback = `Local fallback summary (${language}): ${masked.split("\n").length} lines.`;
      } else {
        fallback = `Local fallback for intent '${intent}'.`;
      }

      setResult(fallback);
    } finally {
      setLoading(false);
    }
  };

  const onSave = async () => {
    setSaveStatus("Saving...");

    const payload = {
      userId: "demo-user",
      chatOriginal: chatText,
      chatMasked: maskedPreview || maskNames(chatText),
      intent,
      language,
      response: result,
    };

    try {
      const res = await fetch("/api/save", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (data.success) setSaveStatus(`Saved (id: ${data.id})`);
      else setSaveStatus("Save failed");
    } catch (err) {
      setSaveStatus("Save failed");
    }
  };

  const speak = () => {
    const text = result || "No result";

    const u = new SpeechSynthesisUtterance(text);
    if (language.toLowerCase().includes("hindi")) u.lang = "hi-IN";
    else if (language.toLowerCase().includes("marathi")) u.lang = "mr-IN";
    else if (language.toLowerCase().includes("bengali")) u.lang = "bn-IN";
    else u.lang = "en-US";

    speechSynthesis.cancel();
    speechSynthesis.speak(u);
  };

  return (
    <div className="p-4 mx-auto bg-slate-900 text-white rounded-xl shadow-xl space-y-4">

      <h1 className="text-lg font-bold">WhatsApp Chat → AI Analysis</h1>

      <div>
        <label className="block text-sm text-slate-400">Paste WhatsApp Chat (max 5000 chars)</label>
        <textarea
          className="w-full mt-1 p-3 rounded-lg bg-slate-800 border border-slate-700 text-sm"
          rows={8}
          maxLength={5000}
          value={chatText}
          onChange={(e) => setChatText(e.target.value)}
        />
        <div className="text-right text-xs text-slate-500">
          {chatText.length} / 5000
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm text-slate-400">Intent</label>
          <select
            className="w-full mt-1 p-3 rounded-lg bg-slate-800 border border-slate-700 text-sm"
            value={intent}
            onChange={(e) => setIntent(e.target.value)}
          >
            <option>Summarize</option>
            <option>Generate Reply</option>
            <option>Extract Action Items</option>
            <option>Sentiment Analysis</option>
            <option>Tone Classification</option>
            <option>Conversation Insights</option>
          </select>
        </div>

        <div>
          <label className="block text-sm text-slate-400">Language</label>
          <select
            className="w-full mt-1 p-3 rounded-lg bg-slate-800 border border-slate-700 text-sm"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          >
            <option>English</option>
            <option>Hindi</option>
            <option>Marathi</option>
            <option>Bengali</option>
            <option>Gujarati</option>
            <option>Kannada</option>
            <option>Telugu</option>
            <option>Tamil</option>
          </select>
        </div>
      </div>

      <button
        onClick={onSend}
        className="w-full py-3 bg-blue-500 rounded-lg text-sm font-semibold"
      >
        {loading ? "Processing..." : "Send to AI"}
      </button>

      <div className="bg-slate-800 p-3 rounded-lg">
        <h3 className="text-sm font-semibold mb-1">Masked Preview</h3>
        <div className="text-slate-300 text-sm whitespace-pre-wrap">
          {maskedPreview || "No masking done yet"}
        </div>
      </div>

      <div className="bg-slate-800 p-3 rounded-lg">
        <h3 className="text-sm font-semibold mb-1">AI Result</h3>
        <div className="text-slate-300 text-sm whitespace-pre-wrap">
          {result || "No result yet"}
        </div>
      </div>

      <div className="flex gap-3">
        <button
          className="flex-1 py-3 bg-green-500 rounded-lg text-sm"
          onClick={onSave}
        >
          Save to TCloud
        </button>

        <button
          className="flex-1 py-3 bg-purple-500 rounded-lg text-sm"
          onClick={speak}
        >
          TTS
        </button>
      </div>

      {saveStatus && (
        <div className="text-xs text-slate-400">{saveStatus}</div>
      )}

      <div className="text-xs text-slate-500">
        This WhatsApp module uses the **same backend** as EmailAnalysis.
      </div>
    </div>
  );
}
